import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiGet, apiPut } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { HelpCircle, Plus, Trash2 } from 'lucide-react';

const textareaClass = 'w-full min-h-[8rem] rounded-xl border border-slate-200 p-3 text-sm font-body';

/** Admin-managed help content: the welcome video, the textbook-scan ask and
 *  guidelines, and the FAQs Study Buddy answers from. */
export default function HelpCenterPanel() {
  const [help, setHelp] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiGet('/admin/settings').then(d => setHelp(d.help_center || {})).catch(() => setHelp({}));
  }, []);

  const set = (key, value) => setHelp(h => ({ ...h, [key]: value }));
  const setFaq = (i, key, value) => setHelp(h => {
    const faqs = [...(h.faqs || [])];
    faqs[i] = { ...faqs[i], [key]: value };
    return { ...h, faqs };
  });
  const setStep = (i, key, value) => setHelp(h => {
    const steps = [...(h.earn_steps || [])];
    steps[i] = { ...steps[i], [key]: value };
    return { ...h, earn_steps: steps };
  });

  const save = async () => {
    setSaving(true);
    try {
      const clean = { ...help,
        faqs: (help.faqs || []).filter(f => (f.q || '').trim() && (f.a || '').trim()),
        earn_steps: (help.earn_steps || []).filter(s => (s.title || '').trim() && (s.body || '').trim()) };
      await apiPut('/admin/settings', { help_center: clean });
      setHelp(clean);
      toast.success('Help content saved — live for students and Study Buddy');
    } catch (e) { toast.error(e?.message || 'Could not save help content'); }
    setSaving(false);
  };

  if (!help) return null;

  return (
    <Card className="p-6 rounded-2xl border-slate-100" data-testid="help-center-card">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-indigo-500" /> Study Buddy Help &amp; FAQs
          </h3>
          <p className="text-xs text-slate-500 font-body mt-1">
            The welcome video, the textbook-scan ask students see when their board isn’t ready,
            the scanning guidelines, and the questions Study Buddy answers on its own.
          </p>
        </div>
        <Button data-testid="save-help-center-btn" size="sm" onClick={save} disabled={saving}
          className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-full font-heading text-xs flex-shrink-0">
          {saving ? 'Saving…' : 'Save help content'}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 p-4 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-heading text-slate-700">Welcome Film (in-app animated, plays once)</Label>
            <p className="text-[11px] text-slate-400 font-body">
              The five-scene animated welcome built into the app — nothing to upload. Shown to every new
              student before setup, unless a real video is switched on below.
            </p>
          </div>
          <Switch data-testid="welcome-film-enabled-switch"
            checked={help.welcome_film_enabled !== false}
            onCheckedChange={v => set('welcome_film_enabled', v)}
            className="data-[state=checked]:bg-emerald-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 p-4 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-heading text-slate-700">Welcome video (overrides the film)</Label>
            <p className="text-[11px] text-slate-400 font-body">
              Switch this on WITH a URL to replace the animated film with your own video. Accepts an
              MP4/WebM file URL or an embed URL.
            </p>
          </div>
          <Switch data-testid="intro-video-enabled-switch"
            checked={help.intro_video_enabled === true}
            onCheckedChange={v => set('intro_video_enabled', v)}
            className="data-[state=checked]:bg-emerald-500" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
          <div>
            <Label className="text-xs font-body text-slate-500">Video URL</Label>
            <Input data-testid="intro-video-url-input" value={help.intro_video_url || ''}
              placeholder="https://…/gurukulveda-intro.mp4"
              onChange={e => set('intro_video_url', e.target.value)}
              className="rounded-lg font-body text-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs font-body text-slate-500">Video title</Label>
            <Input data-testid="intro-video-title-input" value={help.intro_video_title || ''}
              onChange={e => set('intro_video_title', e.target.value)}
              className="rounded-lg font-body text-sm mt-1" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-body text-slate-500">Textbook-scan ask (shown for boards we haven’t built)</Label>
          <textarea data-testid="scan-intro-input" className={`${textareaClass} mt-1`}
            value={help.scan_intro || ''} onChange={e => set('scan_intro', e.target.value)} />
        </div>
        <div>
          <Label className="text-xs font-body text-slate-500">Scanning guidelines (students expand this)</Label>
          <textarea data-testid="scan-guidelines-input" className={`${textareaClass} mt-1`}
            value={help.scan_guidelines || ''} onChange={e => set('scan_guidelines', e.target.value)} />
        </div>
      </div>

      <div className="mt-5">
        <Label className="text-xs font-body text-slate-500">
          Textbook upload policy — who may upload (shown with the rights checkbox students must tick)
        </Label>
        <Input data-testid="upload-policy-title-input" value={help.upload_policy_title || ''}
          placeholder="Who may upload a textbook chapter"
          onChange={e => set('upload_policy_title', e.target.value)}
          className="rounded-lg font-body text-sm mt-1" />
        <textarea data-testid="upload-policy-input" className={`${textareaClass} mt-2`}
          value={help.upload_policy || ''} onChange={e => set('upload_policy', e.target.value)} />
        <p className="text-[11px] text-slate-400 font-body mt-1">
          Keep the line that says approval is <strong>not</strong> permission to redistribute copyrighted
          material — students and rights holders both rely on it.
        </p>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-heading text-slate-700">“How to earn Veda Coins” steps</Label>
          <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-full"
            data-testid="add-earn-step-btn"
            onClick={() => setHelp(h => ({ ...h, earn_steps: [...(h.earn_steps || []), { title: '', body: '' }] }))}>
            <Plus className="w-3 h-3 mr-1" /> Add step
          </Button>
        </div>
        <div className="space-y-3 mt-3">
          {(help.earn_steps || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`earn-step-row-${i}`}>
              <div className="flex items-start gap-2">
                <Input data-testid={`earn-step-title-${i}`} value={s.title || ''} placeholder="Step title"
                  onChange={e => setStep(i, 'title', e.target.value)}
                  className="rounded-lg font-body text-sm" />
                <Button size="sm" variant="ghost" className="h-8 text-rose-600 flex-shrink-0"
                  data-testid={`earn-step-remove-${i}`}
                  onClick={() => setHelp(h => ({ ...h, earn_steps: (h.earn_steps || []).filter((_, j) => j !== i) }))}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <textarea data-testid={`earn-step-body-${i}`} value={s.body || ''} placeholder="What the student should do"
                onChange={e => setStep(i, 'body', e.target.value)}
                className="w-full min-h-[4.5rem] rounded-lg border border-slate-200 p-2.5 text-sm font-body mt-2" />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-heading text-slate-700">FAQs Study Buddy answers</Label>
          <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-full"
            data-testid="add-faq-btn"
            onClick={() => setHelp(h => ({ ...h, faqs: [...(h.faqs || []), { q: '', a: '' }] }))}>
            <Plus className="w-3 h-3 mr-1" /> Add question
          </Button>
        </div>
        <div className="space-y-3 mt-3">
          {(help.faqs || []).map((f, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3" data-testid={`faq-row-${i}`}>
              <div className="flex items-start gap-2">
                <Input data-testid={`faq-q-${i}`} value={f.q || ''} placeholder="Question"
                  onChange={e => setFaq(i, 'q', e.target.value)}
                  className="rounded-lg font-body text-sm" />
                <Button size="sm" variant="ghost" className="h-8 text-rose-600 flex-shrink-0"
                  data-testid={`faq-remove-${i}`}
                  onClick={() => setHelp(h => ({ ...h, faqs: (h.faqs || []).filter((_, j) => j !== i) }))}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <textarea data-testid={`faq-a-${i}`} value={f.a || ''} placeholder="Answer in plain, kind language"
                onChange={e => setFaq(i, 'a', e.target.value)}
                className="w-full min-h-[4.5rem] rounded-lg border border-slate-200 p-2.5 text-sm font-body mt-2" />
            </div>
          ))}
          {!(help.faqs || []).length && (
            <p className="text-xs text-slate-400 font-body">No FAQs yet — add the questions parents and students keep asking.</p>
          )}
        </div>
      </div>
    </Card>
  );
}
